/**
 * 客服 / 私信 聊天 Store
 *
 * @description
 * 统一管理：
 * 1. 客服弹框显隐、浮窗状态
 * 2. 客服当前会话（customerServiceInfo / conversationInfo / sessionList）
 * 3. 私信当前会话（personalSessionInfo / personalSessionList）
 * 4. 弹框与消息中心共用的 Tab（popupTab）及打开时的选中参数
 *
 * MessageChatPanel 组件从此 store 读取/写入会话状态；
 * CustomerServicePopup 仅为 Dialog 外壳。
 */
import { defineStore } from 'pinia'
import type { PersonalChatSession, PopupChatTab } from '~/types/personalChat'

/**
 * 判断当前是否处于消息中心页面 /messages
 * 在消息中心页时不弹出 Dialog，仅更新 store 状态供嵌入的 MessageChatPanel 使用
 */
const isOnMessagesPage = () => {
  if (!import.meta.client) return false
  const route = useRoute()
  const path = route.path.replace(/\/$/, '') || '/'
  return path === '/messages'
}

/** 当前选中的客服顾问资料（来自会话列表 customerServiceInfo） */
export interface CustomerServiceInfo {
  id: number
  uid: number
  nickName: string
  avatar: string
  postTitleName: string
  workTime: string
  phone: string
  wechat: string
  welcomeMessage: string
  skillList: any[]
  isOnline: boolean
}

/**
 * 当前客服会话上下文（选中客服会话后写入）
 * conversationInfo.id 在拉客服历史时作为 groupId 传入
 */
export interface ConversationInfo {
  id: number
  customerId: number
  customerServiceId: number
  /** 悟空 IM channelID，用于匹配实时客服消息 */
  channelId?: string | number
  status: number
  createTime: string
  updateTime: string
  userUnReadMessageCount?: number
}

/** 客服会话列表单项（接口 records 原始结构） */
export interface SessionItem {
  id: number
  userId: number
  customerServiceId: number
  selectServiveId: number
  firstMessageTime: string
  lastMessageTime: string
  lastMessage: string
  userMessageCount: number
  serviceMessageCount: number
  isServiceReplied: number
  isCustomerRead: number
  status: number
  createTime: string
  updateTime: string
  channelId: string
  channelType: number
  userInfo: any
  customerServiceInfo: any
  userUnReadMessageCount: number
  serviceUnReadMessageCount: number
}

interface CustomerServiceState {
  /** 右侧聊天区头部展示的客服资料 */
  customerServiceInfo: CustomerServiceInfo | null
  /** 当前客服会话元数据（含 channelId） */
  conversationInfo: ConversationInfo | null
  /** 全局客服 Dialog 是否可见 */
  isPopupVisible: boolean
  /**
   * 打开客服时指定顾问 ID，用于在会话列表中定位默认选中项
   * 来源：openService({ uid })、路由 query.selectServiveId
   */
  selectServiveId: string | number | null
  /** 是否展示右下角「在线客服」浮标 */
  isServiceOnline: boolean
  /** 客服会话列表缓存 */
  sessionList: SessionItem[]
  /** 当前选中的客服会话 ID */
  currentSessionId: number | null
  /** 左侧 Tab：客服 / 私信（弹框与 /messages 页共用） */
  popupTab: PopupChatTab
  /** 打开私信时指定已有会话 ID（如 IM 通知带 sessionId） */
  selectPersonalSessionId: number | null
  /** 打开私信时指定目标用户，将调用 session/start 创建或获取会话 */
  personalTargetUserId: number | null
  /** 私信会话列表缓存 */
  personalSessionList: PersonalChatSession[]
  /** 当前选中的私信会话 ID */
  currentPersonalSessionId: number | null
  /**
   * 当前私信会话完整对象（含 canSendMore、targetUserInfo）
   * 破冰限制、头部展示均读此字段
   */
  personalSessionInfo: PersonalChatSession | null
}

export const useCustomerServiceStore = defineStore('customerService', {
  state: (): CustomerServiceState => ({
    customerServiceInfo: null,
    conversationInfo: null,
    isPopupVisible: false,
    selectServiveId: null,
    isServiceOnline: false,
    sessionList: [],
    currentSessionId: null,
    popupTab: 'service',
    selectPersonalSessionId: null,
    personalTargetUserId: null,
    personalSessionList: [],
    currentPersonalSessionId: null,
    personalSessionInfo: null,
  }),

  getters: {
    isVisible: (state): boolean => state.isPopupVisible,
  },

  actions: {
    setCustomerServiceInfo(data: CustomerServiceInfo | null) {
      this.customerServiceInfo = data
    },

    setConversationInfo(data: ConversationInfo | null) {
      this.conversationInfo = data
    },

    setSessionList(list: SessionItem[]) {
      this.sessionList = list
    },

    setCurrentSessionId(sessionId: number | null) {
      this.currentSessionId = sessionId
    },

    setPopupTab(tab: PopupChatTab) {
      this.popupTab = tab
    },

    setPersonalSessionList(list: PersonalChatSession[]) {
      this.personalSessionList = list
    },

    setPersonalSessionInfo(session: PersonalChatSession | null) {
      this.personalSessionInfo = session
    },

    setCurrentPersonalSessionId(sessionId: number | null) {
      this.currentPersonalSessionId = sessionId
    },

    /**
     * 打开客服弹框（默认客服 Tab）
     * @param selectServiveId 可选，指定要定位的客服顾问 ID
     * 在 /messages 路由下：只更新 Tab/选中参数，不显示 Dialog
     */
    openPopup(selectServiveId?: string | number) {
      this.popupTab = 'service'
      this.selectPersonalSessionId = null
      this.personalTargetUserId = null
      if (selectServiveId) {
        this.selectServiveId = selectServiveId
      } else {
        this.selectServiveId = null
      }
      if (isOnMessagesPage()) {
        return
      }
      this.isPopupVisible = true
      this.isServiceOnline = true
      localStorage.setItem('isServiceFloat', 'true')
    },

    /**
     * 打开私信弹框（默认私信 Tab）
     * @param options.sessionId 打开已有私信会话
     * @param options.targetUserId 向指定用户发起新私信（调用 startSession）
     * 在 /messages 路由下：只更新状态，不显示 Dialog
     */
    openPersonalPopup(options?: { sessionId?: number; targetUserId?: number }) {
      this.popupTab = 'personal'
      this.selectServiveId = null
      this.selectPersonalSessionId = options?.sessionId ?? null
      this.personalTargetUserId = options?.targetUserId ?? null
      if (isOnMessagesPage()) {
        return
      }
      this.isPopupVisible = true
      this.isServiceOnline = true
      localStorage.setItem('isServiceFloat', 'true')
    },

    closePopup() {
      this.isPopupVisible = false
    },

    /** 切换弹框；在 /messages 且当前未打开时直接 return，避免重复弹窗 */
    togglePopup() {
      if (!this.isPopupVisible && isOnMessagesPage()) {
        return
      }
      this.isPopupVisible = !this.isPopupVisible
    },

    clearServiceFloat() {
      this.isServiceOnline = false
      localStorage.removeItem('isServiceFloat')
    },

    initServiceFloatFromStorage() {
      const isServiceFloat = localStorage.getItem('isServiceFloat')
      if (isServiceFloat === 'true') {
        this.isServiceOnline = true
      }
    },

    reset() {
      this.customerServiceInfo = null
      this.conversationInfo = null
      this.isPopupVisible = false
      this.selectServiveId = null
      this.sessionList = []
      this.currentSessionId = null
      this.popupTab = 'service'
      this.selectPersonalSessionId = null
      this.personalTargetUserId = null
      this.personalSessionList = []
      this.currentPersonalSessionId = null
      this.personalSessionInfo = null
    },
  },
})
